/**
 * TargetingPreview Component - Preview targeting rule results
 *
 * Features:
 * - Displays matched vs total user counts
 * - Debounced API calls on rule changes
 * - Loading and error states
 */

import React, { useState, useEffect, useRef } from 'react';
import * as adminTargetingApi from '../../../api/adminTargeting';
import './TargetingPreview.css';

function TargetingPreview({ rules }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce API calls
    debounceRef.current = setTimeout(async () => {
      // If no rules or empty conditions, show "all users"
      if (!rules || !rules.conditions || rules.conditions.length === 0) {
        setPreview(null);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await adminTargetingApi.preview(rules);
        setPreview(result.data);
      } catch (err) {
        setError(err.message || 'Failed to preview targeting');
        setPreview(null);
      } finally {
        setLoading(false);
      }
    }, 500); // 500ms debounce

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [rules]);

  // No conditions = all users
  if (!rules || !rules.conditions || rules.conditions.length === 0) {
    return (
      <div className="targeting-preview targeting-preview--all">
        <span className="targeting-preview__icon">👥</span>
        <span className="targeting-preview__text">All active users will see this message</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="targeting-preview targeting-preview--loading">
        <span className="targeting-preview__spinner"></span>
        <span className="targeting-preview__text">Calculating...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="targeting-preview targeting-preview--error">
        <span className="targeting-preview__icon">⚠️</span>
        <span className="targeting-preview__text">{error}</span>
      </div>
    );
  }

  if (preview) {
    const percentage = preview.total_users > 0
      ? Math.round((preview.matched_users / preview.total_users) * 100)
      : 0;

    return (
      <div className="targeting-preview targeting-preview--success">
        <span className="targeting-preview__icon">👥</span>
        <span className="targeting-preview__text">
          <strong>{preview.matched_users}</strong> of {preview.total_users} users ({percentage}%) will see this message
        </span>
      </div>
    );
  }

  return null;
}

export default TargetingPreview;
